import {
  Box,
  Button,
  ColorPicker,
  Container,
  Field,
  FileUpload,
  Flex,
  Float,
  HStack,
  Icon,
  Input,
  InputGroup,
  parseColor,
  Portal,
  Select,
  Textarea,
  useListCollection,
  Text,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import TabsList from "../../../components/Common/TabsList"
import { getSettingsCommon, handleError, userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { lazy, useEffect, useMemo, useState } from "react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { ApiError, MediaService, SettingsCommonService, SettingsCommonUpdate } from "../../../client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import useCustomToast from "../../../hooks/useCustomToast"
import { FiFacebook, FiInstagram, FiLinkedin, FiUpload, FiYoutube } from "react-icons/fi"
import { FaXTwitter } from "react-icons/fa6"
import { waitForCelerySuccess } from "../../../utils"

const AuthorsField = lazy(() => import("../../../components/Common/AuthorsField"))

export const Route = createFileRoute("/_layout/settings/")({
  component: SettingsGeneral,
})


function SettingsGeneralContent() {
  // Permission check
  const [sg_read, sg_update, au_read, me_create, me_read] = useMemo(() => {
    const [sg_read, sg_update] = ["read", "update"].map((action) => userHasPermission([{ perm: `settings_common_${action}`, is_allowed: false }])[0].is_allowed)
    const [au_read] =  ["read"].map((action) => userHasPermission([{ perm: `authors_${action}`, is_allowed: false }])[0].is_allowed)
    const [me_create, me_read] = ["create", "read"].map((action) => userHasPermission([{ perm: `media_${action}`, is_allowed: false }])[0].is_allowed)

    return [sg_read, sg_update, au_read, me_create, me_read]
  }, [userHasPermission])

  if(!sg_read) return <NoPermission/>

  // Query
  const queryClient = useQueryClient()
  const showToast = useCustomToast()

  const { data: settingsCommon } = useQuery({
    ...getSettingsCommon(),
  })

  //Form default values
  const defaultValues = useMemo(() => ({
    publication_day: settingsCommon?.publication_day,
    publication_time: settingsCommon?.publication_time,
    circulation_number: settingsCommon?.circulation_number,
    subscription_number: settingsCommon?.subscription_number,
    reader_number: settingsCommon?.reader_number,
    website_title: settingsCommon?.website_title,
    website_description: settingsCommon?.website_description,
    website_domain: settingsCommon?.website_domain,
    website_email: settingsCommon?.website_email,
    main_color: settingsCommon?.main_color,
    secondary_color: settingsCommon?.secondary_color,
    tertiary_color: settingsCommon?.tertiary_color,
    link_facebook: settingsCommon?.link_facebook,
    link_instagram: settingsCommon?.link_instagram,
    link_youtube: settingsCommon?.link_youtube,
    link_linkedin: settingsCommon?.link_linkedin,
    link_twitter: settingsCommon?.link_twitter,
    captcha_id: settingsCommon?.captcha_id,
    captcha_secret: settingsCommon?.captcha_secret,
    replace_string: settingsCommon?.replace_string,
    fk_media_logo_id: settingsCommon?.fk_media_logo?.id,
    fk_media_agb_id: settingsCommon?.fk_media_agb?.id,
    fk_authors_editorials_ids: settingsCommon?.fk_authors_editorials?.map(i => i.id),
    fk_authors_sport_editorials_ids: settingsCommon?.fk_authors_sport_editorials?.map(i => i.id)
  }), [settingsCommon])

  const { collection: publiDay } = useListCollection<{name: string, value: number}>({
    initialItems: [
      {name: "Montag", value: 0},
      {name: "Dienstag", value: 1},
      {name: "Mittwoch", value: 2},
      {name: "Donnerstag", value: 3},
      {name: "Freitag", value: 4},
      {name: "Samstag", value: 5},
      {name: "Sonntag", value: 6},
    ],
    itemToString: (item) => item.name,
    itemToValue: (item) => String(item.value),
  })

  //AGB File
  const [hideAgb, setHideAgb] = useState<boolean>(false)
  const [agbFile, setAgbFile] = useState<File | null>(null)
  const [prevAgbFile, setPrevAgbFile] = useState<File | null>(null)

  const previousAgbFile = () => {
    if(settingsCommon?.fk_media_agb) {
      setHideAgb(true)

      setPrevAgbFile(new File(
        [""],
        settingsCommon.fk_media_agb.file_name,
        { type: settingsCommon.fk_media_agb.mime_type }
      ))
    }
  }

  //Logo File
  const [hideLogo, setHideLogo] = useState<boolean>(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [prevLogoFile, setPrevLogoFile] = useState<File | null>(null)

  const previousLogoFile = () => {
    if(settingsCommon?.fk_media_logo) {
      setHideLogo(true)

      setPrevLogoFile(new File(
        [""],
        settingsCommon.fk_media_logo.file_name,
        { type: settingsCommon.fk_media_logo.mime_type }
      ))
    }
  }

  // Form
  const form = useForm<SettingsCommonUpdate>({
      mode: "onBlur",
      criteriaMode: "all",
      defaultValues: defaultValues,
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: {
      isSubmitting,
      errors,
      isDirty,
    },
  } = form

  useEffect(() => {
    if (!settingsCommon) return

    previousAgbFile()
    previousLogoFile()

    reset(defaultValues)
  }, [settingsCommon])

  // Mutate form data
  const mutation = useMutation({
    mutationFn: (data: SettingsCommonUpdate) =>
      SettingsCommonService.updateSettingsCommon({ requestBody: data }),
    onSuccess: () => {
      showToast("Aktion erfolgreich!", "Einstellungen wurden angepasst", "success")
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["settings-common"] })
    },
  })

  // Mutate file
  const mutationMedia = useMutation({
    mutationFn: (data: File) =>
      MediaService.createMedia({ formData: { file: data } }),
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] })
    },
  })

  // Submit form
  const onSubmit: SubmitHandler<SettingsCommonUpdate> = async (data) => {
    if(agbFile) {
      const media = await mutationMedia.mutateAsync(agbFile)
      data.fk_media_agb_id = media.id
    } else if(!agbFile && !prevAgbFile){
      data.fk_media_agb_id = null
    }

    if(logoFile) {
      const media = await mutationMedia.mutateAsync(logoFile)
      data.fk_media_logo_id = media.id

      if (media.task_id) await waitForCelerySuccess(media.task_id)
    } else if(!logoFile && !prevLogoFile){
      data.fk_media_logo_id = null
    }

    if(data.website_email == "") data.website_email = null

    mutation.mutate(data)

    setAgbFile(null)
    setPrevAgbFile(null)
    setLogoFile(null)
    setPrevLogoFile(null)
  }

  // Cancle form
  const onCancel = () => {
    reset(defaultValues)

    previousAgbFile()
    setAgbFile(null)

    previousLogoFile()
    setLogoFile(null)
  }

  return (
    <Container maxW="full">
      <Box
        w={{ sm: "full", md: "50%" }}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Field.Root mt={4} invalid={!!errors.publication_day} required>
          <Field.Label htmlFor="publication_day">
            Publikations Tag
            <Field.RequiredIndicator />
          </Field.Label>

          <Controller
            control={control}
            name="publication_day"
            render={({ field }) => (
              <Select.Root
                multiple={false}
                name={field.name}
                value={[String(field.value)]}
                onValueChange={({ value }) => field.onChange(Number(value))}
                onInteractOutside={() => field.onBlur()}
                collection={publiDay}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Publikations Tag" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {publiDay.items.map((day) => (
                        <Select.Item item={day} key={day.value}>
                          {day.name}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            )}
          />
          {errors.publication_day && (
            <Field.ErrorText>{errors.publication_day.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.publication_time} required>
          <Field.Label htmlFor="publication_time">
            Publikations Zeit
            <Field.RequiredIndicator />
          </Field.Label>

          <Input
            id="publication_time"
            {...register("publication_time", {
                required: "Publikations Zeit ist erforderlich",
              })
            }
            type="time"
            placeholder="Publikations Zeit"
          />
          {errors.publication_time && (
            <Field.ErrorText>{errors.publication_time.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.circulation_number} required>
          <Field.Label htmlFor="circulation_number">
            Zeitungs Auflage
            <Field.RequiredIndicator />
          </Field.Label>

          <Input
            id="circulation_number"
            {...register("circulation_number", {
                required: "Zeitungs Auflage ist erforderlich",
              })
            }
            type="number"
            placeholder="Zeitungs Auflage"
            min={0}
          />
          {errors.circulation_number && (
            <Field.ErrorText>{errors.circulation_number.message}</Field.ErrorText>
          )}
        </Field.Root>

         <Field.Root mt={4} invalid={!!errors.subscription_number} required>
          <Field.Label htmlFor="subscription_number">
            Anzahl Abonnements
            <Field.RequiredIndicator />
          </Field.Label>
          <Input
            id="subscription_number"
            {...register("subscription_number", {
                required: "Anzahl Abonnements ist erforderlich",
              })
            }
            type="number"
            placeholder="Anzahl Abonnements"
            min={0}
          />
          {errors.subscription_number && (
            <Field.ErrorText>{errors.subscription_number.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.reader_number} required>
          <Field.Label htmlFor="reader_number">
            Anzahl Leser
           <Field.RequiredIndicator />
          </Field.Label>

          <Input
            id="reader_number"
            {...register("reader_number", {
                required: "Anzahl Leser ist erforderlich",
              })
            }
            type="number"
            placeholder="Anzahl Leser"
            min={0}
          />
          {errors.reader_number && (
            <Field.ErrorText>{errors.reader_number.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.fk_authors_editorials_ids}>
          <Field.Label htmlFor="fk_authors_editorials_ids">Redaktion</Field.Label>

            {au_read ? (
              <Controller
                control={control}
                name="fk_authors_editorials_ids"
                render={({ field }) => (
                  <AuthorsField field={field} />
                )}
              />
            ) : (
              <Text color="danger">Keine Berechtigung für Autoren</Text>
            )}

            {errors.fk_authors_editorials_ids && (
              <Field.ErrorText>{errors.fk_authors_editorials_ids.message}</Field.ErrorText>
            )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.fk_authors_sport_editorials_ids}>
          <Field.Label htmlFor="fk_authors_sport_editorials_ids">Sport-Redaktion</Field.Label>

          {au_read ? (
            <Controller
              control={control}
              name="fk_authors_sport_editorials_ids"
              render={({ field }) => (
                <AuthorsField field={field} />
              )}
            />
          ) : (
            <Text color="danger">Keine Berechtigung für Autoren</Text>
          )}

          {errors.fk_authors_sport_editorials_ids && (
            <Field.ErrorText>{errors.fk_authors_sport_editorials_ids.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.website_title} required>
          <Field.Label htmlFor="website_title">
            Webseiten Titel
            <Field.RequiredIndicator />
          </Field.Label>

          <Input
            id="website_title"
            {...register("website_title", {
                required: "Webseiten Titel ist erforderlich",
                maxLength: {
                  value: 255,
                  message: "Webseiten Titel darf maximal 255 Zeichen lang sein",
                },
              })
            }
            type="text"
            placeholder="Webseiten Titel"
          />
          {errors.website_title && (
            <Field.ErrorText>{errors.website_title.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.website_description}>
          <Field.Label htmlFor="website_description">Webseiten Beschreibung</Field.Label>
            <Textarea
              id="website_description"
              {...register("website_description", {
                  maxLength: {
                    value: 255,
                    message: "Webseiten Beschreibung darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              placeholder="Webseiten Beschreibung"
            />
            {errors.website_description && (
              <Field.ErrorText>{errors.website_description.message}</Field.ErrorText>
            )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.website_domain} required>
          <Field.Label htmlFor="website_domain">
            Webseiten Domain
            <Field.RequiredIndicator />
          </Field.Label>

          <Input
            id="website_domain"
            {...register("website_domain", {
                required: "Webseiten Domain ist erforderlich",
                maxLength: {
                  value: 255,
                  message: "Webseiten Domain darf maximal 255 Zeichen lang sein",
                },
              })
            }
            type="text"
            placeholder="Webseiten Domain"
          />
          {errors.website_domain && (
            <Field.ErrorText>{errors.website_domain.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.website_email}>
          <Field.Label htmlFor="website_email">Webseiten E-Mail</Field.Label>

          <Input
            id="website_email"
            {...register("website_email", {
                maxLength: {
                  value: 255,
                  message: "Webseiten E-Mail darf maximal 255 Zeichen lang sein"
                },
              })
            }
            type="email"
            placeholder="Webseiten E-Mail"
          />
          {errors.website_email && (
            <Field.ErrorText>{errors.website_email.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.main_color} required>
          <Field.Label htmlFor="main_color">
            Hauptfarbe
            <Field.RequiredIndicator />
          </Field.Label>

          <Controller
            name="main_color"
            control={control}
            render={({ field }) => (
              <ColorPicker.Root
                name={field.name}
                value={field.value ? parseColor(field.value) : undefined}
                onValueChange={(e) => field.onChange(e.value.toString("hex"))}
              >
                <ColorPicker.HiddenInput />
                <ColorPicker.Control>
                  <ColorPicker.Input data-testid="main_color" />
                  <ColorPicker.Trigger />
                </ColorPicker.Control>
                <Portal>
                  <ColorPicker.Positioner>
                    <ColorPicker.Content>
                      <ColorPicker.Area />
                      <HStack>
                        <ColorPicker.EyeDropper size="sm" variant="outline" />
                        <ColorPicker.Sliders />
                      </HStack>
                    </ColorPicker.Content>
                  </ColorPicker.Positioner>
                </Portal>
              </ColorPicker.Root>
            )}
          />
          {errors.main_color && (
            <Field.ErrorText>{errors.main_color.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.secondary_color} required>
          <Field.Label htmlFor="secondary_color">
            Sekundärfarbe
            <Field.RequiredIndicator />
          </Field.Label>

          <Controller
            name="secondary_color"
            control={control}
            render={({ field }) => (
              <ColorPicker.Root
                name={field.name}
                value={field.value ? parseColor(field.value) : undefined}
                onValueChange={(e) => field.onChange(e.value.toString("hex"))}
              >
                <ColorPicker.HiddenInput />
                <ColorPicker.Control>
                  <ColorPicker.Input data-testid="secondary_color" />
                  <ColorPicker.Trigger />
                </ColorPicker.Control>
                <Portal>
                  <ColorPicker.Positioner>
                    <ColorPicker.Content>
                      <ColorPicker.Area />
                      <HStack>
                        <ColorPicker.EyeDropper size="sm" variant="outline" />
                        <ColorPicker.Sliders />
                      </HStack>
                    </ColorPicker.Content>
                  </ColorPicker.Positioner>
                </Portal>
              </ColorPicker.Root>
            )}
          />
          {errors.secondary_color && (
            <Field.ErrorText>{errors.secondary_color.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.tertiary_color} required>
          <Field.Label htmlFor="tertiary_color">
            Kontrastfarbe
            <Field.RequiredIndicator />
          </Field.Label>

          <Controller
            name="tertiary_color"
            control={control}
            render={({ field }) => (
              <ColorPicker.Root
                name={field.name}
                value={field.value ? parseColor(field.value) : undefined}
                onValueChange={(e) => field.onChange(e.value.toString("hex"))}
              >
                <ColorPicker.HiddenInput />
                <ColorPicker.Control>
                  <ColorPicker.Input data-testid="tertiary_color" />
                  <ColorPicker.Trigger />
                </ColorPicker.Control>
                <Portal>
                  <ColorPicker.Positioner>
                    <ColorPicker.Content>
                      <ColorPicker.Area />
                      <HStack>
                        <ColorPicker.EyeDropper size="sm" variant="outline" />
                        <ColorPicker.Sliders />
                      </HStack>
                    </ColorPicker.Content>
                  </ColorPicker.Positioner>
                </Portal>
              </ColorPicker.Root>
            )}
          />
          {errors.tertiary_color && (
            <Field.ErrorText>{errors.tertiary_color.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.link_facebook}>
          <Field.Label htmlFor="link_facebook">Facebook Link</Field.Label>
          <InputGroup startElement={<FiFacebook />}>
            <Input
              id="link_facebook"
              {...register("link_facebook", {
                  maxLength: {
                    value: 255,
                    message: "Facebook Link darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              type="url"
              placeholder="Facebook Link"
            />
          </InputGroup>

          {errors.link_facebook && (
            <Field.ErrorText>{errors.link_facebook.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.link_instagram}>
          <Field.Label htmlFor="link_instagram">Instagram Link</Field.Label>
          <InputGroup startElement={<FiInstagram />}>
            <Input
              id="link_instagram"
              {...register("link_instagram", {
                  maxLength: {
                    value: 255,
                    message: "Instagram Link darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              type="url"
              placeholder="Instagram Link"
            />
          </InputGroup>

          {errors.link_instagram && (
            <Field.ErrorText>{errors.link_instagram.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.link_youtube}>
          <Field.Label htmlFor="link_youtube">YouTube Link</Field.Label>
          <InputGroup startElement={<FiYoutube />}>
            <Input
              id="link_youtube"
              {...register("link_youtube", {
                  maxLength: {
                    value: 255,
                    message: "YouTube Link darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              type="url"
              placeholder="YouTube Link"
            />
          </InputGroup>

          {errors.link_youtube && (
            <Field.ErrorText>{errors.link_youtube.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.link_linkedin}>
          <Field.Label htmlFor="link_linkedin">LinkedIn Link</Field.Label>
          <InputGroup startElement={<FiLinkedin />}>
            <Input
              id="link_linkedin"
              {...register("link_linkedin", {
                  maxLength: {
                    value: 255,
                    message: "LinkedIn Link darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              type="url"
              placeholder="LinkedIn Link"
            />
          </InputGroup>

          {errors.link_linkedin && (
            <Field.ErrorText>{errors.link_linkedin.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.link_twitter}>
          <Field.Label htmlFor="link_twitter">Twitter Link</Field.Label>
          <InputGroup startElement={<FaXTwitter />}>
            <Input
              id="link_twitter"
              {...register("link_twitter", {
                  maxLength: {
                    value: 255,
                    message: "Twitter Link darf maximal 255 Zeichen lang sein",
                  },
                })
              }
              type="url"
              placeholder="Twitter Link"
            />
          </InputGroup>

          {errors.link_twitter && (
            <Field.ErrorText>{errors.link_twitter.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.captcha_id}>
          <Field.Label htmlFor="captcha_id">Captcha ID</Field.Label>

          <Input
            id="captcha_id"
            {...register("captcha_id", {
                maxLength: {
                  value: 255,
                  message: "Captcha ID darf maximal 255 Zeichen lang sein",
                },
              })
            }
            type="text"
            placeholder="Captcha ID"
          />

          {errors.captcha_id && (
            <Field.ErrorText>{errors.captcha_id.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.captcha_secret}>
          <Field.Label htmlFor="captcha_secret">Captcha Secret</Field.Label>

          <Input
            id="captcha_secret"
            {...register("captcha_secret", {
                maxLength: {
                  value: 255,
                  message: "Captcha Secret darf maximal 255 Zeichen lang sein",
                },
              })
            }
            type="text"
            placeholder="Captcha Secret"
          />

          {errors.captcha_secret && (
            <Field.ErrorText>{errors.captcha_secret.message}</Field.ErrorText>
          )}
        </Field.Root>


        <Field.Root mt={4} invalid={!!errors.fk_media_agb_id}>
          <Field.Label htmlFor="fk_media_agb_id">AGB (PDF)</Field.Label>

          <FileUpload.Root
            maxW="xl"
            alignItems="stretch"
            maxFiles={1}
            accept={["application/pdf"]}
            >

            {me_create ? (
              <>
                <FileUpload.HiddenInput
                  data-testid="fk_media_agb_id"
                  onChange={(e) => {
                    setHideAgb(e.target.value ? true : false)
                    setAgbFile(e.target.files ? e.target.files[0] : null)
                    setValue("fk_media_agb_id", e.target.files ? "" : null, { shouldDirty: true })
                  }}
                />

                <FileUpload.Dropzone
                  w="full"
                  minH="fit-content"
                  py={8}
                  backgroundColor="unset"
                  borderRadius={5}
                  _hover={{_light: {bgColor: "secondary"}, _dark: {bgColor: "dark"}}}
                  hidden={hideAgb}
                  data-testid="fk_media_agb_id_dropzone"
                >
                  <Icon as={FiUpload} size="md" color="dim" />
                  <FileUpload.DropzoneContent>
                    <Box>Hier klicken oder hineinziehen um ein PDF hochzuladen</Box>
                  </FileUpload.DropzoneContent>
                </FileUpload.Dropzone>
              </>
            ) : (
              <Text color="danger">Keine Berechtigung um Bilder zu erstellen</Text>
            )}

            {me_read ? (
              prevAgbFile ? (
                <FileUpload.ItemGroup>
                  <FileUpload.Item key={prevAgbFile.name} file={prevAgbFile}>
                    <FileUpload.ItemPreview />
                    <FileUpload.ItemName />
                    <FileUpload.ItemDeleteTrigger
                      onClick={() => {
                        setPrevAgbFile(null)
                        setHideAgb(false)
                        setValue("fk_media_agb_id", null, { shouldDirty: true })
                      }}
                    />
                  </FileUpload.Item>
                </FileUpload.ItemGroup>
              ) : (
                <FileUpload.List clearable />
              )
            ) : (
              <Text color="danger">Keine Berechtigung um Bilder anzusehen</Text>
            )}
          </FileUpload.Root>

          {errors.fk_media_agb_id && (
            <Field.ErrorText>{errors.fk_media_agb_id.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root mt={4} invalid={!!errors.fk_media_logo_id}>
          <Field.Label htmlFor="fk_media_logo_id">Webseiten Logo</Field.Label>
            <FileUpload.Root
              maxW="xl"
              alignItems="stretch"
              maxFiles={1}
              accept={["image/jpg","image/jpeg","image/png","image/svg+xml"]}
            >
              {me_create ? (
                <>
                  <FileUpload.HiddenInput
                    data-testid="fk_media_logo_id"
                    onChange={(e) => {
                      setHideLogo(e.target.value ? true : false)
                      setLogoFile(e.target.files ? e.target.files[0] : null)
                      setValue("fk_media_logo_id", e.target.files ? "" : null, { shouldDirty: true })
                    }}
                  />

                  <FileUpload.Dropzone
                    w="full"
                    minH="fit-content"
                    py={8}
                    backgroundColor="unset"
                    borderRadius={5}
                    _hover={{_light: {bgColor: "secondary"}, _dark: {bgColor: "dark"}}}
                    hidden={hideLogo}
                    data-testid="fk_media_logo_id_dropzone"
                  >
                    <Icon as={FiUpload} size="md" color="dim" />
                    <FileUpload.DropzoneContent>
                      <Box>Hier klicken oder hineinziehen um ein Bild hochzuladen</Box>
                    </FileUpload.DropzoneContent>
                  </FileUpload.Dropzone>
                </>
              ) : (
                <Text color="danger">Keine Berechtigung um Bilder zu erstellen</Text>
              )}

              {me_read ? (
                prevLogoFile ? (
                  <FileUpload.ItemGroup>
                    <FileUpload.Item
                      key={prevLogoFile.name}
                      file={prevLogoFile}
                      flexDir="column"
                      alignItems="flex-start"
                    >
                      <FileUpload.ItemName />
                      <FileUpload.ItemPreviewImage
                        src={settingsCommon?.fk_media_logo?.path.replace(/-(LG)\./, "-SM.")}
                        alt="preview"
                      />
                      <Float placement="top-end">
                        <FileUpload.ItemDeleteTrigger
                          boxSize="4"
                          layerStyle="fill.solid"
                          bgColor="main"
                          onClick={() => {
                            setPrevLogoFile(null)
                            setHideLogo(false)
                            setValue("fk_media_logo_id", null, { shouldDirty: true })
                          }}
                        />
                      </Float>
                    </FileUpload.Item>
                  </FileUpload.ItemGroup>
                ) : (
                  <FileUpload.ItemGroup>
                    <FileUpload.Context>
                      {({ acceptedFiles }) =>
                        acceptedFiles.map((file) => (
                          <FileUpload.Item key={file.name} file={file}>
                            <FileUpload.ItemPreviewImage />
                            <Float placement="top-end">
                              <FileUpload.ItemDeleteTrigger 
                                boxSize="4" 
                                layerStyle="fill.solid" 
                                bgColor="main" 
                                borderRadius={3}
                              />
                            </Float>
                          </FileUpload.Item>
                        ))
                      }
                    </FileUpload.Context>
                  </FileUpload.ItemGroup>
                )
              ) : (
                <Text color="danger">Keine Berechtigung um Bilder anzusehen</Text>
              )}
            </FileUpload.Root>

            {errors.fk_media_logo_id && (
              <Field.ErrorText>{errors.fk_media_logo_id.message}</Field.ErrorText>
            )}
        </Field.Root>


        <Field.Root mt={4} mb={8} invalid={!!errors.replace_string}>
          <Field.Label htmlFor="replace_string">Indesign Zeichen</Field.Label>
            <Textarea
              id="replace_string"
              {...register("replace_string")}
              placeholder="Indesign Zeichen"
            />
            {errors.replace_string && (
              <Field.ErrorText>{errors.replace_string.message}</Field.ErrorText>
            )}
        </Field.Root>

        <Flex mt={4} mb={8} gap={8}>
          <Button
            variant="primary"
            type="submit"
            loading={isSubmitting}
            disabled={!isDirty || !sg_update}
          >
            Speichern
          </Button>
          <Button onClick={onCancel} disabled={isSubmitting || !isDirty} variant="secondary">
            Abbrechen
          </Button>
        </Flex>
      </Box>
    </Container>
  )
}

export default SettingsGeneralContent

function SettingsGeneral() {
  const tabs = useMemo(() => [
    { value: "settings", title: "Allgemein" },
    { value: "settings/navigation", title: "Navigation" },
    { value: "settings/landingpage", title: "Startseite" },
    { value: "settings/mimetypes", title: "Dateitypen" },
    { value: "settings/subscriptions", title: "Abos" },
    { value: "settings/priocategories", title: "Kategorien" },
    { value: "settings/ratecharts", title: "Tariftabellen" },
    { value: "settings/actions", title: "Aktionen" },
  ], [])

  return <TabsList tabs={tabs} defaultVal="settings" />
}
